import React from 'react';
import { labeledCheckboxStyles as styles } from './LabeledCheckbox.styles';

/**
 * Generic "I agree to <terms>" acceptance row: a checkbox plus a label that
 * may optionally include a trailing inline link.
 *
 *
 * Any id passed via `checkboxId` MUST render on the <input> element itself
 * because the SCA Selenium suite asserts on it. Callers currently owning ids:
 *   - "class-booking-accept-terms"          (book-event.js)
 *   - "class-booking-accept-terms"          (book-appointment.js)
 *   - "purchase-membership-accept-terms"    (buy-membership.js, purchase terms)
 */
export type LabeledCheckboxData = {
  accepted?: boolean;
  agreeLabel?: string;
  linkLabel?: string;
  checkboxId?: string;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  onClickLink?: () => void;
  dataAttributes?: Record<string, string>;
  /** @deprecated use `linkLabel` */
  payGTermsLabel?: string;
  /** @deprecated use `onClickLink` */
  onClickTerms?: () => void;
};

type Props = {
  data: LabeledCheckboxData;
};

export function LabeledCheckbox({ data }: Props) {
  if (!data) {
    return null;
  }

  const {
    accepted = false,
    agreeLabel = '',
    linkLabel,
    checkboxId,
    disabled = false,
    onChange,
    onClickLink,
    dataAttributes,
    payGTermsLabel,
    onClickTerms,
  } = data;

  const extraInputProps: Record<string, string> = {};
  if (dataAttributes) {
    Object.keys(dataAttributes).forEach((key) => {
      if (key.indexOf('data-') === 0) {
        extraInputProps[key] = dataAttributes[key];
      }
    });
  }

  const resolvedLinkLabel = linkLabel ?? payGTermsLabel;
  const resolvedOnClickLink = onClickLink ?? onClickTerms;
  const hasLink = !!resolvedLinkLabel && !!resolvedOnClickLink;

  const trimmedAgreeLabel = typeof agreeLabel === 'string' ? agreeLabel.trim() : '';
  if (!trimmedAgreeLabel && !hasLink) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  const handleClickLink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (resolvedOnClickLink) {
      resolvedOnClickLink();
    }
  };

  const boxStyle = disabled ? { ...styles.box, ...styles.boxDisabled } : styles.box;
  const inputStyle = disabled ? { ...styles.input, ...styles.inputDisabled } : styles.input;

  return (
    <div className="labeled-checkbox" style={styles.wrapper}>
      <span style={boxStyle}>
        <input
          id={checkboxId || undefined}
          type="checkbox"
          style={inputStyle}
          checked={accepted}
          disabled={disabled}
          onChange={handleChange}
          {...extraInputProps}
        />
        {accepted ? (
          <svg style={styles.check} viewBox="0 0 16 16" aria-hidden="true">
            <polyline
              points="3.5,8.5 6.5,11.5 12.5,4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        ) : null}
      </span>
      <span style={styles.text}>
        <label htmlFor={checkboxId || undefined} style={styles.label}>
          {hasLink ? `${agreeLabel} ` : agreeLabel}
        </label>
        {hasLink ? (
          <a href="" style={styles.link} onClick={handleClickLink}>
            {resolvedLinkLabel}
          </a>
        ) : null}
      </span>
    </div>
  );
}
